import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessScheduleContext = createContext();

// FIX: Moved DAY_NAMES outside the component so it doesn't need to be a dependency
const DAY_NAMES = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday'
};

export const useBusinessSchedule = () => {
    const context = useContext(BusinessScheduleContext);
    if (!context) {
        throw new Error('useBusinessSchedule must be used within a BusinessScheduleProvider');
    }
    return context;
};

export const BusinessScheduleProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const { makeAuthenticatedRequest, loginType } = useAuth();

    // API base URL
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

    // Determine the correct base URL based on login type
    const getServiceBaseUrl = useCallback(() => {
        if (loginType === 'master') {
            return `${API_BASE_URL}/master/business-schedules/api/v1/business-schedules`;
        } else if (loginType === 'admin') {
            return `${API_BASE_URL}/admin/business-schedules/api/v1/business-schedules`;
        } else {
            throw new Error('Invalid login type for business schedule management');
        }
    }, [API_BASE_URL, loginType]);

    // Helper function to handle API responses
    const handleApiResponse = async (response) => {
        const data = await response.json();

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(data.error || 'Schedule not found');
            } else if (response.status === 400) {
                throw new Error(data.error || 'Invalid request data');
            } else if (response.status === 409) {
                throw new Error(data.error || 'Schedule already exists for this business and day');
            } else if (response.status === 500) {
                throw new Error(data.error || 'Internal server error');
            }
            throw new Error(data.error || data.message || 'Request failed');
        }

        return data;
    };

    // Clear error state
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Validate admin/master access
    const validateAccess = useCallback(() => {
        if (loginType !== 'admin' && loginType !== 'master') {
            throw new Error('Only admin and master users can manage business schedules');
        }
    }, [loginType]);

    // -------------------------------------------------------------------------
    // GET ALL SCHEDULES (with pagination and filters)
    // -------------------------------------------------------------------------
    const getAllSchedules = useCallback(async (options = {}) => {
        validateAccess();

        const {
            limit = 50,
            offset = 0,
            businessRegistryNumber,
            dayOfWeek,
            isClosed,
            is24Hours
        } = options;

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const params = new URLSearchParams();

            params.append('limit', limit.toString());
            params.append('offset', offset.toString());

            if (businessRegistryNumber) params.append('business_registry_number', businessRegistryNumber);
            if (dayOfWeek !== undefined && dayOfWeek !== null) params.append('day_of_week', dayOfWeek.toString());
            if (isClosed !== undefined && isClosed !== null) params.append('is_closed', isClosed.toString());
            if (is24Hours !== undefined && is24Hours !== null) params.append('is_24_hours', is24Hours.toString());

            const response = await makeAuthenticatedRequest(`${baseUrl}?${params.toString()}`, {
                method: 'GET',
            });

            const result = await handleApiResponse(response);
            setSchedules(result.schedules || []);
            setTotalCount(result.total_count || 0);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to fetch schedules';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // GET SCHEDULE BY ID
    // -------------------------------------------------------------------------
    const getScheduleById = useCallback(async (id) => {
        validateAccess();

        if (!id) throw new Error('Schedule ID is required');

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/${id}`, {
                method: 'GET',
            });

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to fetch schedule';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // GET SCHEDULES BY BUSINESS
    // -------------------------------------------------------------------------
    const getSchedulesByBusiness = useCallback(async (businessRegistryNumber) => {
        validateAccess();

        if (!businessRegistryNumber) throw new Error('Business Registry Number is required');

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/business/${businessRegistryNumber}`, {
                method: 'GET',
            });

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to fetch business schedules';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // CREATE SCHEDULE
    // -------------------------------------------------------------------------
    const createSchedule = useCallback(async (scheduleData) => {
        validateAccess();

        const {
            businessRegistryNumber,
            dayOfWeek,
            openTime,
            closeTime,
            isClosed = false,
            is24Hours = false,
            editedBy
        } = scheduleData;

        if (!businessRegistryNumber) throw new Error('Business Registry Number is required');
        if (dayOfWeek === undefined || dayOfWeek === null) throw new Error('Day of week is required');
        if (dayOfWeek < 0 || dayOfWeek > 6) throw new Error('Day of week must be between 0 (Sunday) and 6 (Saturday)');
        if (!openTime) throw new Error('Open time is required');
        if (!closeTime) throw new Error('Close time is required');

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const payload = {
                business_registry_number: businessRegistryNumber,
                day_of_week: parseInt(dayOfWeek, 10),
                open_time: openTime,
                close_time: closeTime,
                is_closed: isClosed,
                is_24_hours: is24Hours,
            };

            if (editedBy) payload.edited_by = editedBy;

            const response = await makeAuthenticatedRequest(baseUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to create schedule';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // BULK CREATE/UPDATE SCHEDULES
    // -------------------------------------------------------------------------
    const bulkCreateSchedules = useCallback(async (businessRegistryNumber, schedulesArray, editedBy) => {
        validateAccess();

        if (!businessRegistryNumber) throw new Error('Business Registry Number is required');
        if (!schedulesArray || schedulesArray.length === 0) throw new Error('Schedules array is required');

        // Validate each schedule in the array
        for (const schedule of schedulesArray) {
            if (schedule.dayOfWeek === undefined || schedule.dayOfWeek === null) {
                throw new Error('Each schedule must have a day_of_week');
            }
            if (schedule.dayOfWeek < 0 || schedule.dayOfWeek > 6) {
                throw new Error('Day of week must be between 0 (Sunday) and 6 (Saturday)');
            }
            if (!schedule.openTime) throw new Error('Each schedule must have an open_time');
            if (!schedule.closeTime) throw new Error('Each schedule must have a close_time');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const payload = {
                business_registry_number: businessRegistryNumber,
                schedules: schedulesArray.map(s => ({
                    day_of_week: parseInt(s.dayOfWeek, 10),
                    open_time: s.openTime,
                    close_time: s.closeTime,
                    is_closed: s.isClosed || false,
                    is_24_hours: s.is24Hours || false,
                })),
            };

            if (editedBy) payload.edited_by = editedBy;

            const response = await makeAuthenticatedRequest(`${baseUrl}/bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to bulk create schedules';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // UPDATE SCHEDULE BY ID
    // -------------------------------------------------------------------------
    const updateSchedule = useCallback(async (id, updateData) => {
        validateAccess();

        if (!id) throw new Error('Schedule ID is required');

        const { openTime, closeTime, isClosed, is24Hours, editedBy } = updateData;

        // At least one field should be provided
        if (openTime === undefined && closeTime === undefined && isClosed === undefined && is24Hours === undefined) {
            throw new Error('At least one field to update is required');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const payload = {};

            if (openTime !== undefined) payload.open_time = openTime;
            if (closeTime !== undefined) payload.close_time = closeTime;
            if (isClosed !== undefined) payload.is_closed = isClosed;
            if (is24Hours !== undefined) payload.is_24_hours = is24Hours;
            if (editedBy) payload.edited_by = editedBy;

            const response = await makeAuthenticatedRequest(`${baseUrl}/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to update schedule';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);


    // -------------------------------------------------------------------------
    // UPDATE SCHEDULE BY BUSINESS AND DAY
    // -------------------------------------------------------------------------
    const updateScheduleByBusinessAndDay = useCallback(async (businessRegistryNumber, dayOfWeek, updateData) => {
        validateAccess();

        if (!businessRegistryNumber) throw new Error('Business Registry Number is required');
        if (dayOfWeek === undefined || dayOfWeek === null) throw new Error('Day of week is required');
        if (dayOfWeek < 0 || dayOfWeek > 6) throw new Error('Day of week must be between 0 (Sunday) and 6 (Saturday)');

        const { openTime, closeTime, isClosed, is24Hours, editedBy } = updateData;

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const payload = {};

            // Convert to booleans explicitly
            const is24HoursBool = is24Hours === true || is24Hours === 'true';
            const isClosedBool = isClosed === true || isClosed === 'true';

            if (is24HoursBool) {
                payload.is_24_hours = true;
                payload.is_closed = false;
                payload.open_time = '00:00';
                payload.close_time = '23:59';
            } else if (isClosedBool) {
                payload.is_closed = true;
                payload.is_24_hours = false;
                payload.open_time = '09:00';
                payload.close_time = '17:00';
            } else {
                // Normal schedule
                payload.is_closed = false;
                payload.is_24_hours = false;

                // Validate and format times
                let cleanOpenTime = openTime ? String(openTime).trim() : '';
                let cleanCloseTime = closeTime ? String(closeTime).trim() : '';

                // FIX: Handle cases where a full ISO string might have been passed (e.g., "2026-01-01T09:00:00")
                if (cleanOpenTime.includes('T')) cleanOpenTime = cleanOpenTime.split('T')[1];
                if (cleanCloseTime.includes('T')) cleanCloseTime = cleanCloseTime.split('T')[1];

                // Ensure we only look at the first 5 characters (HH:MM)
                cleanOpenTime = cleanOpenTime.substring(0, 5);
                cleanCloseTime = cleanCloseTime.substring(0, 5);

                // Strict Regex for HH:MM (00:00 to 23:59)
                const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

                if (!cleanOpenTime || !timeRegex.test(cleanOpenTime)) {
                    throw new Error(`Invalid open_time format: "${cleanOpenTime}". Please use HH:MM format.`);
                }
                if (!cleanCloseTime || !timeRegex.test(cleanCloseTime)) {
                    throw new Error(`Invalid close_time format: "${cleanCloseTime}". Please use HH:MM format.`);
                }

                payload.open_time = cleanOpenTime;
                payload.close_time = cleanCloseTime;
            }

            if (editedBy) payload.edited_by = editedBy;

            const response = await makeAuthenticatedRequest(
                `${baseUrl}/business/${businessRegistryNumber}/day/${dayOfWeek}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                }
            );

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to update schedule';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // DELETE SCHEDULE BY ID
    // -------------------------------------------------------------------------
    const deleteSchedule = useCallback(async (id) => {
        validateAccess();

        if (!id) throw new Error('Schedule ID is required');

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/${id}`, {
                method: 'DELETE',
            });

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to delete schedule';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // DELETE ALL SCHEDULES FOR A BUSINESS
    // -------------------------------------------------------------------------
    const deleteSchedulesByBusiness = useCallback(async (businessRegistryNumber) => {
        validateAccess();

        if (!businessRegistryNumber) throw new Error('Business Registry Number is required');

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/business/${businessRegistryNumber}`, {
                method: 'DELETE',
            });

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to delete business schedules';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // -------------------------------------------------------------------------
    // UTILITY FUNCTIONS
    // -------------------------------------------------------------------------

    // Create a default week schedule (Mon-Fri 9-5, Sat-Sun closed)
    const createDefaultWeekSchedule = useCallback((businessRegistryNumber, editedBy) => {
        const defaultSchedules = [
            { dayOfWeek: 0, openTime: '10:00:00', closeTime: '17:00:00', isClosed: true, is24Hours: false },  // Sunday - Closed
            { dayOfWeek: 1, openTime: '09:00:00', closeTime: '17:00:00', isClosed: false, is24Hours: false }, // Monday
            { dayOfWeek: 2, openTime: '09:00:00', closeTime: '17:00:00', isClosed: false, is24Hours: false }, // Tuesday
            { dayOfWeek: 3, openTime: '09:00:00', closeTime: '17:00:00', isClosed: false, is24Hours: false }, // Wednesday
            { dayOfWeek: 4, openTime: '09:00:00', closeTime: '17:00:00', isClosed: false, is24Hours: false }, // Thursday
            { dayOfWeek: 5, openTime: '09:00:00', closeTime: '17:00:00', isClosed: false, is24Hours: false }, // Friday
            { dayOfWeek: 6, openTime: '10:00:00', closeTime: '17:00:00', isClosed: true, is24Hours: false },  // Saturday - Closed
        ];

        return bulkCreateSchedules(businessRegistryNumber, defaultSchedules, editedBy);
    }, [bulkCreateSchedules]);

    // Get day name from day number
    // FIX: No missing dependency here now because DAY_NAMES is global
    const getDayName = useCallback((dayOfWeek) => {
        return DAY_NAMES[dayOfWeek] || 'Unknown';
    }, []);

    // Format time for display (HH:MM:SS -> HH:MM AM/PM)
    const formatTimeForDisplay = useCallback((timeString) => {
        if (!timeString) return '';
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
    }, []);

    // Get endpoint info for debugging
    // FIX: No missing dependency here now
    const getEndpointInfo = useCallback(() => {
        return {
            loginType: loginType || 'none',
            managementUrl: loginType === 'master'
                ? `${API_BASE_URL}/master/business-schedules/api/v1/business-schedules`
                : loginType === 'admin'
                    ? `${API_BASE_URL}/admin/business-schedules/api/v1/business-schedules`
                    : 'N/A (requires login)',
            dayNames: DAY_NAMES
        };
    }, [loginType, API_BASE_URL]);

    const value = {
        // State
        loading,
        error,
        schedules,
        totalCount,
        clearError,

        // CRUD Actions
        getAllSchedules,
        getScheduleById,
        getSchedulesByBusiness,
        createSchedule,
        bulkCreateSchedules,
        updateSchedule,
        updateScheduleByBusinessAndDay,
        deleteSchedule,
        deleteSchedulesByBusiness,

        // Utilities
        createDefaultWeekSchedule,
        getDayName,
        formatTimeForDisplay,
        getEndpointInfo,
        DAY_NAMES,

        // Permissions
        hasManagementAccess: loginType === 'admin' || loginType === 'master',
    };

    return (
        <BusinessScheduleContext.Provider value={value}>
            {children}
        </BusinessScheduleContext.Provider>
    );
};